<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { call } from '../api/http'
import { BILL_TABS, isOverdue, listPayables, payables, type BillTab, type Line, type Payable } from '../api/payables'
import { openDocumentFile } from '../api/documents'
import { useSession } from '../stores/session'
import { compact, useForm } from '../lib/form'
import { formatMoney, isoDaysAgo } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import UiDrawer from '../components/UiDrawer.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const route = useRoute()
const load = useForm()
const create = useForm()
const action = useForm()

const qTab = route.query.tab
const tab = ref<BillTab>(BILL_TABS.includes(qTab as BillTab) ? (qTab as BillTab) : 'open')
const q = ref('')
const sortAsc = ref(true)
const items = ref<Payable[]>([])
const cursor = ref<string | undefined>()

async function fetchPage(append: boolean) {
  await load.submit(async () => {
    const page = await listPayables(tab.value, q.value, append ? cursor.value : undefined)
    items.value = append ? [...items.value, ...page.items] : page.items
    cursor.value = page.next_cursor
  })
}
watch([() => session.workspace?.id, tab, q], () => fetchPage(false), { immediate: true })

// The API orders by due date descending; the toggle re-sorts the loaded rows, undated last.
const sorted = computed(() => {
  const key = (p: Payable) => p.due_date ?? p.issue_date ?? ''
  return [...items.value].sort((a, b) => {
    const ka = key(a)
    const kb = key(b)
    if (!ka || !kb) return ka ? -1 : kb ? 1 : 0
    return sortAsc.value ? ka.localeCompare(kb) : kb.localeCompare(ka)
  })
})

const statusVariant = (p: Payable) =>
  isOverdue(p) ? 'danger' : p.status === 'paid' ? 'success' : p.status === 'draft' ? 'outline' : 'secondary'

// ---- create ----
const showCreate = ref(false)
const form = reactive({ vendor: '', invoice_number: '', issue_date: '', due_date: '', amount: '', currency: 'EUR', category: '', note: '', status: 'open' as 'draft' | 'open' })

/** "12.34" / "12,34" → 1234 minor units. ponytail: assumes 2-decimal currencies (EUR); JPY etc. would need the currency's exponent. */
const toMinor = (s: string) => Math.round(Number(s.replace(',', '.')) * 100)

async function onCreate() {
  const { amount, currency, ...rest } = form
  const ok = await create.submit(async () => {
    const p = await call(payables.POST('/payables', { body: compact({ ...rest, total: { amount_minor: toMinor(amount), currency: currency.toUpperCase() } }) }))
    if (tab.value === 'all' || tab.value === p.status) items.value = [p, ...items.value]
  })
  if (ok) {
    Object.assign(form, { vendor: '', invoice_number: '', issue_date: '', due_date: '', amount: '', category: '', note: '' })
    showCreate.value = false
  }
}

// ---- detail ----
const selected = ref<Payable | null>(null)
const lines = ref<Line[]>([])
const edit = reactive({ note: '', category: '' })
const paidAt = ref(isoDaysAgo(0))
const detail = useForm()

async function open(p: Payable) {
  selected.value = p
  lines.value = []
  Object.assign(edit, { note: p.note ?? '', category: p.category ?? '' })
  paidAt.value = isoDaysAgo(0)
  await detail.submit(async () => (lines.value = await call(payables.GET('/payables/{id}/lines', { params: { path: { id: p.id } } }))))
}

function replace(p: Payable) {
  items.value = items.value.map((x) => (x.id === p.id ? p : x))
  selected.value = p
}

const path = (p: Payable) => ({ params: { path: { id: p.id } } })
const confirmBill = (p: Payable) => action.submit(async () => replace(await call(payables.POST('/payables/{id}/confirm', path(p)))))
const markPaid = (p: Payable) =>
  action.submit(async () => replace(await call(payables.POST('/payables/{id}/paid', { ...path(p), body: { paid_at: paidAt.value } }))))
const saveNote = (p: Payable) =>
  action.submit(async () => replace(await call(payables.PATCH('/payables/{id}', { ...path(p), body: { note: edit.note, category: edit.category } }))))

async function cancelBill(p: Payable) {
  if (!confirm(t('bills.cancelConfirm', { vendor: p.vendor }))) return
  await action.submit(async () => replace(await call(payables.POST('/payables/{id}/cancel', path(p)))))
}

async function remove(p: Payable) {
  if (!confirm(t('bills.deleteConfirm', { vendor: p.vendor }))) return
  const ok = await action.submit(() => call(payables.DELETE('/payables/{id}', path(p))))
  if (ok) {
    items.value = items.value.filter((x) => x.id !== p.id)
    selected.value = null
  }
}

const openDoc = (id: string) => action.submit(() => openDocumentFile(id))
const docIds = (p: Payable) => (p.document_ids.length ? p.document_ids : p.document_id ? [p.document_id] : [])
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('bills.title') }}</h1>
        <p class="muted">{{ $t('bills.subtitle') }}</p>
      </div>
      <UiButton v-if="session.canWrite && !showCreate" data-testid="new-bill" @click="showCreate = true">{{ $t('bills.new') }}</UiButton>
    </header>

    <UiCard v-if="showCreate" :title="$t('bills.new')">
      <form class="grid-2" data-testid="create-bill" @submit.prevent="onCreate">
        <UiField :label="$t('bills.vendor')" :error="create.fieldErr('vendor')">
          <input v-model.trim="form.vendor" name="vendor" required maxlength="200" />
        </UiField>
        <UiField :label="$t('bills.invoiceNumber')" :error="create.fieldErr('invoice_number')">
          <input v-model.trim="form.invoice_number" name="invoice_number" maxlength="100" />
        </UiField>
        <UiField :label="$t('bills.issueDate')" :error="create.fieldErr('issue_date')">
          <input v-model="form.issue_date" name="issue_date" type="date" />
        </UiField>
        <UiField :label="$t('bills.dueDate')" :error="create.fieldErr('due_date')">
          <input v-model="form.due_date" name="due_date" type="date" />
        </UiField>
        <UiField :label="$t('bills.amount')" :error="create.fieldErr('total.amount_minor')">
          <input v-model.trim="form.amount" name="amount" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <UiField :label="$t('accounts.currency')" :error="create.fieldErr('total.currency')">
          <input v-model.trim="form.currency" name="currency" required pattern="[A-Za-z]{3}" maxlength="3" />
        </UiField>
        <UiField :label="$t('transactions.category')" :error="create.fieldErr('category')">
          <input v-model.trim="form.category" name="category" maxlength="32" />
        </UiField>
        <UiField :label="$t('bills.status')">
          <select v-model="form.status" name="status">
            <option value="open">{{ $t('bills.statuses.open') }}</option>
            <option value="draft">{{ $t('bills.statuses.draft') }}</option>
          </select>
        </UiField>
        <UiField :label="$t('bills.note')" :error="create.fieldErr('note')">
          <input v-model.trim="form.note" name="note" maxlength="1000" />
        </UiField>
        <div class="row">
          <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
          <UiButton variant="ghost" @click="showCreate = false">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
      <ErrorBanner :error="create.error.value" />
    </UiCard>

    <div class="tabs" role="tablist" data-testid="bill-tabs">
      <button v-for="tb in BILL_TABS" :key="tb" role="tab" type="button" :aria-selected="tab === tb" :data-testid="`bill-tab-${tb}`" @click="tab = tb">
        {{ $t(`bills.tabs.${tb}`) }}
      </button>
    </div>

    <div class="row">
      <input v-model.lazy.trim="q" type="search" class="search" :placeholder="$t('bills.search')" :aria-label="$t('bills.search')" />
      <UiButton variant="outline" size="sm" @click="sortAsc = !sortAsc">{{ $t('bills.dueDate') }} {{ sortAsc ? '↑' : '↓' }}</UiButton>
    </div>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard>
      <p v-if="load.busy.value && !items.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!items.length" :title="$t('bills.empty')" :message="$t('bills.emptyText')">
        <RouterLink v-if="session.canWrite" to="/documents">{{ $t('nav.documents') }}</RouterLink>
      </EmptyState>
      <div v-else class="table-wrap">
        <table class="table" data-testid="bill-table">
          <thead>
            <tr>
              <th>{{ $t('bills.vendor') }}</th>
              <th>{{ $t('bills.dueDate') }}</th>
              <th>{{ $t('bills.status') }}</th>
              <th class="num">{{ $t('bills.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in sorted" :key="p.id" :class="['clickable', { overdue: isOverdue(p) }]" data-testid="bill-row" @click="open(p)">
              <td>
                <strong>{{ p.vendor }}</strong>
                <div class="muted small">{{ p.invoice_number ?? '' }} {{ p.category ? `· ${p.category}` : '' }}</div>
              </td>
              <td class="date">{{ p.due_date ?? '—' }}</td>
              <td>
                <UiBadge :variant="statusVariant(p)" data-testid="bill-status">
                  {{ isOverdue(p) ? $t('bills.tabs.overdue') : $t(`bills.statuses.${p.status}`) }}
                </UiBadge>
              </td>
              <td class="num">{{ formatMoney(p.total, locale) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="cursor" class="more">
        <UiButton variant="outline" :loading="load.busy.value" @click="fetchPage(true)">{{ $t('transactions.loadMore') }}</UiButton>
      </div>
    </UiCard>

    <UiDrawer v-if="selected" :title="selected.vendor" @close="selected = null">
      <div class="row">
        <UiBadge :variant="statusVariant(selected)" data-testid="bill-detail-status">{{ $t(`bills.statuses.${selected.status}`) }}</UiBadge>
        <UiBadge v-if="isOverdue(selected)" variant="danger">{{ $t('bills.tabs.overdue') }}</UiBadge>
        <span class="muted small">{{ $t(`bills.sources.${selected.source}`) }}</span>
      </div>
      <ErrorBanner :error="action.error.value ?? detail.error.value" />

      <dl class="kv">
        <dt>{{ $t('bills.amount') }}</dt>
        <dd>{{ formatMoney(selected.total, locale) }}</dd>
        <template v-if="selected.vat">
          <dt>{{ $t('bills.vat') }}</dt>
          <dd>{{ selected.vat.rate_pct }}% · {{ formatMoney({ amount_minor: selected.vat.vat_minor, currency: selected.total.currency }, locale) }}</dd>
        </template>
        <dt>{{ $t('bills.invoiceNumber') }}</dt>
        <dd>{{ selected.invoice_number ?? '—' }}</dd>
        <dt>{{ $t('bills.issueDate') }}</dt>
        <dd>{{ selected.issue_date ?? '—' }}</dd>
        <dt>{{ $t('bills.dueDate') }}</dt>
        <dd>{{ selected.due_date ?? '—' }}</dd>
        <dt>{{ $t('bills.period') }}</dt>
        <dd>{{ selected.period_start ? `${selected.period_start} – ${selected.period_end ?? ''}` : '—' }}</dd>
        <template v-if="selected.paid_at">
          <dt>{{ $t('bills.paidAt') }}</dt>
          <dd>{{ selected.paid_at.slice(0, 10) }}</dd>
        </template>
      </dl>

      <div v-if="session.canWrite" class="row">
        <UiButton v-if="selected.status === 'draft'" data-testid="bill-confirm" :loading="action.busy.value" @click="confirmBill(selected)">
          {{ $t('bills.confirm') }}
        </UiButton>
        <template v-if="selected.status === 'open'">
          <input v-model="paidAt" type="date" class="date-in" :aria-label="$t('bills.paidAt')" />
          <UiButton data-testid="bill-paid" :loading="action.busy.value" @click="markPaid(selected)">{{ $t('bills.markPaid') }}</UiButton>
        </template>
        <UiButton v-if="selected.status === 'draft' || selected.status === 'open'" variant="outline" @click="cancelBill(selected)">
          {{ $t('bills.cancelBill') }}
        </UiButton>
        <UiButton v-if="selected.status !== 'paid'" variant="destructive" @click="remove(selected)">{{ $t('common.delete') }}</UiButton>
      </div>

      <form v-if="session.canWrite" class="grid-2" @submit.prevent="saveNote(selected)">
        <UiField :label="$t('transactions.category')">
          <input v-model.trim="edit.category" maxlength="32" />
        </UiField>
        <UiField :label="$t('bills.note')">
          <input v-model.trim="edit.note" maxlength="1000" />
        </UiField>
        <div><UiButton type="submit" variant="outline" size="sm" :loading="action.busy.value">{{ $t('common.save') }}</UiButton></div>
      </form>
      <p v-else-if="selected.note" class="small">{{ selected.note }}</p>

      <h3>{{ $t('bills.lines') }}</h3>
      <p v-if="detail.busy.value" class="muted">{{ $t('common.loading') }}</p>
      <p v-else-if="!lines.length" class="muted small">—</p>
      <div v-else class="table-wrap">
        <table class="table" data-testid="bill-lines">
          <thead>
            <tr>
              <th>{{ $t('bills.description') }}</th>
              <th class="num">{{ $t('bills.qty') }}</th>
              <th>{{ $t('bills.unit') }}</th>
              <th class="num">{{ $t('bills.unitPrice') }}</th>
              <th class="num">{{ $t('bills.amount') }}</th>
              <th class="num">{{ $t('bills.vatPct') }}</th>
              <th>{{ $t('bills.period') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in lines" :key="l.id">
              <td>{{ l.description }}</td>
              <td class="num">{{ l.qty ?? '' }}</td>
              <td>{{ l.unit ?? '' }}</td>
              <td class="num">{{ l.unit_price ?? '' }}</td>
              <td class="num">{{ formatMoney(l.amount, locale) }}</td>
              <td class="num">{{ l.vat_rate_pct != null ? `${l.vat_rate_pct}%` : '' }}</td>
              <td class="small">{{ l.period_start ? `${l.period_start} – ${l.period_end ?? ''}` : '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <template v-if="docIds(selected).length">
        <h3>{{ $t('bills.documents') }}</h3>
        <div class="row">
          <UiButton v-for="(id, i) in docIds(selected)" :key="id" variant="outline" size="sm" @click="openDoc(id)">
            {{ $t('documents.openFile') }} {{ docIds(selected).length > 1 ? i + 1 : '' }}
          </UiButton>
          <RouterLink to="/documents" class="small">{{ $t('nav.documents') }}</RouterLink>
        </div>
      </template>
    </UiDrawer>
  </div>
</template>

<style scoped>
.wide {
  max-width: 1200px;
}
.search {
  flex: 1;
  min-width: 200px;
}
.clickable {
  cursor: pointer;
}
.clickable:hover td {
  background: var(--bg);
}
.overdue td {
  background: rgb(var(--loss-rgb) / 0.06);
}
.overdue .date {
  color: var(--loss);
  font-weight: 500;
}
.date {
  white-space: nowrap;
}
.date-in {
  width: auto;
}
.more {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}
</style>
